<template>
  <div class="themes--container">
      <data-container>
        <template #controls>
                <AppDropdown
                    placeholder="Select"
                    v-model="filter.sort_by"
                    :options="sortItems"
                    @input="fetchThemes"
                >
                    Sort by
                </AppDropdown>
                <div class="right--controls">
                    <AppButton modifier="small-primary" @click="showThemeForm = true">
                        Add Theme
                    </AppButton>
                    <AppTextField
                        v-model="filter.keyword"
                        placeholder="Type here"
                        icon-right="search"
                        @input="search"
                    />
                </div>
        </template>
        <template #content>
            <AppTable
                :dataset="themeList.data"
                :fields="fields"
                :selected="selectedItems"
                @select="selectedItems = $event"
                @endOfScroll="message('end of scroll')"
                :loading="isLoading"
                :total-count="themeList.total"
                :current-page="parseInt(filter.page)"
                :items-per-page="parseInt(filter.count)"
                @changePage="changePage"
            >
                <template #image="{ props }">
                <img class="icon" :src="`${themePath}/theme/${props.item.image}`" alt="">
                </template>
                <template #created_at="{ props }">
                    {{ props.item.created_at | dateFormatter }}
                </template>
                <template #action="{ props }">
                    <div class="actions">
                        <AppButton modifier="icon small-primary" @click="edit(props.item)">
                            <img src="@/assets/img/ic-edit.svg" class="icon">
                        </AppButton>
                    </div>
                </template>
            </AppTable>

            <AppPopup
                type="success"
                :visible="showThemeForm"
                @close="confirmCloseForm"
            >
                <AppLoading
                    class="loading"
                    v-if="btnLoading"
                ></AppLoading>
                <div class="theme-form" v-else>
                    <h3>{{ isUpdatingTheme ? 'Update' : 'Add' }} Theme</h3>
                    <AppImageUpload placeholder="Click here to upload an Image or Icon"
                        v-model="form.image.value"
                        :error="errors.image"
                        :loading="isImageLoading"
                        @error="errors.image = $event"
                        @input="uploadImage"
                        @remove="form.image = { server_name: '', value: null }"

                    />
                    <AppTextField
                        v-model.trim="form.name"
                        :error="errors.name"
                    >
                        Title
                    </AppTextField>
                    <AppTextField
                        v-model.trim="form.description"
                        :error="errors.description"
                    >
                        Description
                    </AppTextField>
                    <hr>

                    <div class="actions">
                        <AppButton modifier="small-danger" @click="confirmCloseForm"
                            :disabled="isImageLoading"
                        >
                            Close
                        </AppButton>
                        <AppButton modifier="small-primary" @click="submitTheme"
                            :disabled="isImageLoading"
                        >
                            Confirm
                        </AppButton>
                    </div>
                </div>
            </AppPopup>
        </template>
    </data-container>
  </div>
</template>

<script>
import DataContainer from '@/components/DataContainer'
import AppImageUpload from '@/components/AppImageUpload'
import AppDropdown from '@/components/AppDropdown'
import AppButton from '@/components/AppButton'
import AppTextField from '@/components/AppTextField'
import AppTable from '@/components/AppTable'
import AppPopup from '@/components/AppPopup'
import AppLoading from '@/components/AppLoading'
import theme from '@/store/theme/app.js'
import { errorMessage, setQueryParams } from '@/utils/helpers'
import { themeFields, sortItems, rawThemeErrors } from '@/utils/meta.js'
import { mapGetters } from 'vuex'
import { mapFields } from 'vuex-map-fields'

export default {
	data () {
		return {
			showThemeForm: false,
			isUpdatingTheme: false,
			selectedItems: [],
			themePath: process.env.VUE_APP_PATH_FOLDER
		}
	},
	methods: {
		fetchThemes () {
		    const query = { ...this.filter }
			Object.keys(query).forEach(key => {
				!query[key] && (delete query[key])
			})
			this.$router.push({ query })
			const queryStr = setQueryParams(this.filter)
		    this.$store.dispatch('theme/fetchThemes', queryStr)
		},
		search (val) {
			this.filter.page = 1
			this.filter.keyword = val
			clearTimeout(this.timeout)
			this.timeout = setTimeout(() => {
				this.fetchThemes()
			}, 300)
		},
		submitTheme () {
			if (this.validate()) {
				const form = new FormData()
				for (const key in this.form) {
					if (key === 'image') {
						form.set(key, this.form[key].server_name)
						continue
					}
					form.set(key, this.form[key])
				}
				this.isUpdatingTheme && form.set('id', this.form.id)
				const callback = () => {
					this.filter.page = 1
					this.fetchThemes()
					this.showThemeForm = false

					this.$store.dispatch('toggleBanner', {
						message: `Theme has been successfully ${this.isUpdatingTheme ? 'updated' : 'added'}.`,
						type: 'success'
					})
					this.isUpdatingTheme = false
				}

				this.$store.dispatch(`theme/${this.isUpdatingTheme ? 'update' : 'create'}`, {
					form,
					callback
				})
			}
		},
		uploadImage () {
			const form = new FormData()
			form.set('file', this.form.image.value)
			form.set('document_type', 'theme')
			this.$store.dispatch('theme/tempUpload', form)
		},
		validate () {
			this.errors = { ...rawThemeErrors }
			for (const key in this.form) {
				const value = this.form[key]
				if (value === null || (typeof value === 'string' && !value.trim().length)) {
					this.errors[key] = errorMessage.required(key)
				} else if (key === 'image' && !this.form[key].server_name) {
					this.errors[key] = errorMessage.required(key)
				}
			}
			return Object.keys(this.errors).every(key => !this.errors[key])
		},
		edit ({ id, name }) {
			this.showThemeForm = true
			this.isUpdatingTheme = true
			this.$store.dispatch('theme/view', id)
		},
		closeThemeForm () {
			this.showThemeForm = false
			this.isUpdatingTheme = false
			this.$store.commit('theme/clearFormState')
		},
		confirmCloseForm () {
			if (this.hasValue) {
				if (this.isUpdatingTheme && !this.hasChanged) return this.closeThemeForm()

				this.$store.commit('toggleModal', {
					modalName: 'confirm',
					modalType: 'warning',
					modalTitle: 'Warning',
					modalDesc: 'Your input will be lost.',
					confirm: () => {
						if (this.form.image.server_name) {
							const form = new FormData()
							form.append('server_name', this.form.image.server_name)
							form.append('document_type', 'theme')
							this.$store.dispatch('cancelUpload', form)
						}
			            this.errors = { ...rawThemeErrors }
						this.$store.commit('closeSpecificModal', { modalName: 'confirm' })
						this.closeThemeForm()
					}
				})
			} else this.closeThemeForm()
		},

		changePage ({ currentPage, itemsPerPage }) {
			this.filter.page = currentPage
			itemsPerPage !== this.filter.count && (this.filter.page = 1)
			this.filter.count = itemsPerPage
			this.fetchThemes()
		}
	},
	computed: {
		...mapGetters('theme', [
			'themeList',
			'isLoading',
			'btnLoading',
			'isImageLoading',
			'hasChanged'
		]),
		...mapFields('theme', [
			'filter',
			'form',
			'lastViewForm',
			'errors'
		]),
		hasValue () {
			return Object.values(this.form).some(item => {
				if (typeof item === 'object') {
					return item.server_name !== ''
				}

				return item !== ''
			})
		},
		fields () { return themeFields },
		sortItems () { return sortItems }
	},
	created () {
		const query = { ...this.$route.query }
		Object.keys(query).length && (this.filter = { ...query })
		this.fetchThemes()
	},
	beforeCreate () {
		if (!this.$store.hasModule('theme')) {
			this.$store.registerModule('theme', theme)
		}
	},
	components: {
		DataContainer,
		AppDropdown,
		AppLoading,
		AppButton,
		AppTextField,
		AppTable,
		AppPopup,
		AppImageUpload
	}
}
</script>

<style lang="scss" scoped>
.themes--container {
    padding: 1.5em;
}

.theme-form {
    width: 100%;
    .app-text-field {
        margin: 16px 0;
        width: 100%;
        color: darken($default, 10%);
    }

    .actions {
        display: flex;
        justify-content: flex-end;
        margin-top: 16px;

        .app-button:not(:last-of-type) {
            margin-right: 8px;
        }
    }

    h3 {
        color: $default;
    }
}

.app-dropdown {
    min-width: 120px;
}

.custom-table {
    margin-top: 12px;
}

.right--controls{
    margin-top: 16px;
    display: flex;
    justify-content: flex-end;
    flex-grow: 1;

    .app-button--small-primary {
        margin-right: 12px;
    }
}

.icon {
    width: 4em;
}

/deep/ .app-popup {
    width: 30em;
    padding: 16px 0;
}

.app-image-upload {
    width: 100%;
    height: 120px;
    margin-top: 10px;
    ::v-deep label p {
        color: #117DBC;
    }
}
</style>
